import { Injectable } from '@angular/core';
import { migratePhotoBlobs, isMigrationComplete } from '@database/migrations/migrate-photo-blobs';

/**
 * Service d'initialisation de la base de données
 * 
 * Gère les migrations automatiques au démarrage de l'application
 */
@Injectable({
	providedIn: 'root'
})
export class DbInitService {
	private migrationStarted = false;
	private migrationPromise: Promise<void> | null = null;

	/**
	 * Initialise les bases de données et exécute les migrations nécessaires
	 * 
	 * Cette méthode doit être appelée au démarrage de l'application
	 * Elle s'exécute de manière asynchrone et n'est jamais appelée deux fois
	 */
	async initialize(): Promise<void> {
		// Éviter les exécutions multiples
		if (this.migrationStarted) {
			return this.migrationPromise || Promise.resolve();
		}

		this.migrationStarted = true;
		
		this.migrationPromise = this.runMigrations();
		
		return this.migrationPromise;
	}

	private async runMigrations(): Promise<void> {
		console.log('🚀 Initialisation des bases de données...');
		
		try {
			// 1. Migration photo_blobs vers photoDB
			const isComplete = await isMigrationComplete();
			
			if (!isComplete) {
				console.log('📦 Migration photo_blobs nécessaire, démarrage...');
				const stats = await migratePhotoBlobs();
				
				if (stats.migrated > 0) {
					console.log(`✅ Migration photo_blobs terminée: ${stats.migrated} blobs migrés, ${stats.failed} échecs`);
				} else if (stats.skipped > 0) {
					console.log(`✅ Migration photo_blobs déjà effectuée (${stats.skipped} blobs)`);
				} else {
					console.log('✅ Migration photo_blobs: aucune donnée à migrer');
				}
			} else {
				console.log('✅ Migration photo_blobs déjà complète');
			}

			// Autres migrations futures peuvent être ajoutées ici
			
			console.log('✅ Initialisation des bases de données terminée');
			
		} catch (error) {
			console.error('❌ Erreur lors de l\'initialisation des bases de données:', error);
			// Ne pas bloquer l'application en cas d'erreur de migration
			// Les données restent dans l'ancienne DB et peuvent être migrées plus tard
		}
	}

	/**
	 * Force une réinitialisation de la migration (pour tests/debug)
	 */
	async resetMigrationState(): Promise<void> {
		this.migrationStarted = false;
		this.migrationPromise = null;
	}
}
